/**
 * useMilestones Hook
 *
 * React Query hooks for milestone operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  milestonesService,
  CreateMilestoneDto,
  UpdateMilestoneDto,
} from '../services/milestones.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

import type { Milestone } from '@medsuite/models';

export const milestoneKeys = {
  all: ['milestones'] as const,
  lists: () => [...milestoneKeys.all, 'list'] as const,
  list: (investmentId: number) => [...milestoneKeys.lists(), investmentId] as const,
  details: () => [...milestoneKeys.all, 'detail'] as const,
  detail: (investmentId: number, milestoneId: number) =>
    [...milestoneKeys.details(), investmentId, milestoneId] as const,
};

/**
 * Fetch all milestones for an investment
 */
export function useMilestones(
  investmentId: number,
  options?: Omit<UseQueryOptions<Milestone[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Milestone[], Error>({
    queryKey: milestoneKeys.list(investmentId),
    queryFn: async () => {
      const response = await milestonesService.getAll(investmentId);
      // Extract data from success wrapper: {success: true, data: {investment_id, milestones, total}, meta: {}}
      const responseData = unwrapResponse<{ milestones?: Milestone[] }>(response.data);
      // The API returns {investment_id, milestones, total}, extract the milestones array
      return responseData.milestones || [];
    },
    ...options,
  });
}

/**
 * Create new milestone
 */
export function useCreateMilestone(
  investmentId: number,
  options?: UseMutationOptions<Milestone, Error, Omit<CreateMilestoneDto, 'investment_id'>>
) {
  const queryClient = useQueryClient();

  return useMutation<Milestone, Error, Omit<CreateMilestoneDto, 'investment_id'>>({
    mutationFn: async (data) => {
      const response = await milestonesService.create(investmentId, data);
      return unwrapResponse<Milestone>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: milestoneKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}

/**
 * Update milestone
 */
export function useUpdateMilestone(
  investmentId: number,
  options?: UseMutationOptions<Milestone, Error, { milestoneId: number; data: UpdateMilestoneDto }>
) {
  const queryClient = useQueryClient();

  return useMutation<Milestone, Error, { milestoneId: number; data: UpdateMilestoneDto }>({
    mutationFn: async ({ milestoneId, data }) => {
      const response = await milestonesService.update(investmentId, milestoneId, data);
      return unwrapResponse<Milestone>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: milestoneKeys.list(investmentId) });
    },
    ...options,
  });
}

/**
 * Delete milestone
 */
export function useDeleteMilestone(
  investmentId: number,
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (milestoneId) => {
      await milestonesService.delete(investmentId, milestoneId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: milestoneKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
    },
    ...options,
  });
}

/**
 * Reorder milestones
 */
export function useReorderMilestones(
  investmentId: number,
  options?: UseMutationOptions<Milestone[], Error, number[]>
) {
  const queryClient = useQueryClient();

  return useMutation<Milestone[], Error, number[]>({
    mutationFn: async (milestoneIds) => {
      const response = await milestonesService.reorder(investmentId, milestoneIds);
      return unwrapResponse<Milestone[]>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: milestoneKeys.list(investmentId) });
    },
    ...options,
  });
}
